import GlassCard from "@/assets/images/report/glass/card.webp";
import GlassClock from "@/assets/images/report/glass/clock.webp";
import GlassEye from "@/assets/images/report/glass/eye.webp";
import GlassGraphics from "@/assets/images/report/glass/graphics.webp";
import GlassLightning from "@/assets/images/report/glass/lightning.webp";
import GlassNotebook from "@/assets/images/report/glass/notebook.webp";
import GlassUsers from "@/assets/images/report/glass/users.webp";
import GlassWallet from "@/assets/images/report/glass/wallet.webp";
import { Box } from "@repo/components/src/ui/box";
import { HStack } from "@repo/components/src/ui/hstack";
import {
  formatMoney,
  formatMoneyFromFragment,
} from "@repo/components/src/utils/money";
import {
  formatPercentage,
  formatSeparators,
} from "@repo/components/src/utils/numbers";
import { CampaignAnalyticFragmentFragment } from "@repo/graphql/documents/campaign/fragment.generated";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import React from "react";
import KpiCard from "../shared/kpiCard";

dayjs.extend(duration);

type Props = {
  report: CampaignAnalyticFragmentFragment;
};

const KpiGrid = React.memo(({ report }: Props) => {
  const totalViews = report.totalViews;
  const totalReach = report.totalReach;
  const totalInteractions = report.totalInteractions;
  const er = report.engagementRate;
  const investmentMoney = report.investment;
  const investmentLabel = formatMoney(investmentMoney);
  // Approved content is not published content: divide by the pieces that
  // actually reached an audience, not by the ones a reviewer signed off on.
  const avgCostPerVideo = report.avgPerPiecePublished;

  const timeToDeliverSec = report.timeToDeliver;
  const timeToVideoDays = timeToDeliverSec
    ? Math.round(dayjs.duration(timeToDeliverSec, "seconds").asDays() * 10) / 10
    : null;

  // Gift cards and canje products enter `investment` at their retail face value
  // while `cashInvestment` counts only money that left the brand's account. ROI
  // divides an EMV estimate by the whole figure, so a campaign paid purely in
  // product reads as a heavy loss unless the card says what it is comparing.
  const hasProductInvestment = report.productInvestment != null;
  const isCanjeOnly = hasProductInvestment && report.cashInvestment == null;

  const cards = [
    {
      image: GlassUsers,
      tone: "brand" as const,
      label: "Reach",
      value: formatSeparators(totalReach),
      unit: "cuentas",
      sub: "Cuentas únicas alcanzadas por las publicaciones",
    },
    {
      image: GlassEye,
      tone: "info" as const,
      label: "Impresiones",
      value: formatSeparators(totalViews),
      sub: "Total de veces que el contenido fue mostrado",
    },
    {
      image: GlassLightning,
      tone: "success" as const,
      label: "Engagement Rate",
      value: formatPercentage(er),
      sub: `${formatSeparators(totalInteractions)} interacciones totales`,
    },
    {
      image: GlassClock,
      tone: "warning" as const,
      label: "Tiempo a video",
      value: timeToVideoDays !== null ? `${timeToVideoDays}` : "—",
      unit: timeToVideoDays !== null ? "días" : undefined,
      sub: "Promedio desde envío de ODT a publicación",
    },
    {
      image: GlassWallet,
      tone: "teal" as const,
      label: "Inversión total",
      value: investmentLabel,
      sub: `Costo promedio por video: ${formatMoneyFromFragment(avgCostPerVideo)}`,
    },
    {
      image: GlassCard,
      tone: "warning" as const,
      label: "CPM efectivo",
      value: formatMoney(report.cpm),
      sub: "Costo por cada mil impresiones",
    },
    {
      image: GlassNotebook,
      tone: "pink" as const,
      label: "Earned Media Value",
      value: formatMoneyFromFragment(report.emv),
      sub: "Lo que estas impresiones habrían costado en pauta",
    },
    {
      image: GlassGraphics,
      tone: "brand" as const,
      label: isCanjeOnly ? "ROI vs. canje" : "ROI",
      value: formatPercentage(report.roi ?? 0),
      sub: hasProductInvestment
        ? "El canje se valoriza a precio retail, no a su costo"
        : "Retorno sobre la inversión publicitaria",
    },
  ];

  return (
    <HStack className="flex-wrap -m-1.5">
      {cards.map((card) => (
        <Box key={card.label} className="w-1/4 p-1.5">
          <KpiCard {...card} />
        </Box>
      ))}
    </HStack>
  );
});

KpiGrid.displayName = "OrganicKpiGrid";

export default KpiGrid;
